import { KeywordItemType } from '@/api/type/commonTypes';
import { Images } from '@/assets';
import { fonts } from '@/theme/fonts';
import { getTextWidth } from '@/utils/name/getTextWidth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

interface SelectListProps {
  options: KeywordItemType[];
  selectedOption: KeywordItemType;
  onSelect: (item: KeywordItemType) => void;
}
const SelectList = ({ options, selectedOption, onSelect }: SelectListProps) => {
  const [open, setOpen] = useState(false);
  const [maxWidth, setMaxWidth] = useState<number>(0);
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** KeywordType 선택 */
  const handleSelect = useCallback(
    (keywordType: KeywordItemType) => {
      onSelect(keywordType);
      setOpen(false);
    },
    [onSelect]
  );

  useEffect(() => {
    const font = '14px Pretendard';
    const widths = options.map((o) => getTextWidth(t(o.title), font));
    const longest = Math.max(...widths);
    setMaxWidth(longest + 20 + 18 + 12 + 20); // 패딩 + 아이콘 + gap(12px)
  }, [options, t]);

  return (
    <SelectWrapper ref={ref} $width={maxWidth}>
      <Select onClick={() => setOpen((prev) => !prev)}>
        <div>{t(selectedOption.title)}</div>
        <DownArrowImg
          src={Images.SelectDownArrowImg}
          alt="select-down-arrow"
          $open={open}
        />
      </Select>

      {open && (
        <ListSection $width={maxWidth}>
          {options
            .filter((item) => item.keyword !== selectedOption.keyword)
            .map((item) => (
              <SelectItem key={item.keyword} onClick={() => handleSelect(item)}>
                {t(item.title)}
              </SelectItem>
            ))}
        </ListSection>
      )}
    </SelectWrapper>
  );
};

export default SelectList;

interface SelectWrapperProps {
  $width: number;
}

const SelectWrapper = styled.div<SelectWrapperProps>`
  position: relative;
  width: ${({ $width }) => `${$width}px`};
  min-width: 137px;
  font-family: ${fonts.family.poppins};
  font-size: 14px;
  line-height: 120%;
`;

const Select = styled.div`
  position: relative;
  z-index: 30;
  border-radius: 20px;
  border: 3px solid #7299f7;
  background: #fff;
  height: 41px;
  padding: 0 25px 0 17px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const DownArrowImg = styled.img<{ $open: boolean }>`
  width: 18px;
  height: 11px;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0)')};
`;

interface ListSectionProps {
  $width: number;
}

const ListSection = styled.div<ListSectionProps>`
  position: absolute;
  top: 10px;
  left: 0;

  width: ${({ $width }) => `${$width}px`};
  min-width: 100%;
  box-sizing: border-box;

  border-radius: 20px;
  border: 3px solid #7299f7;
  background: #fff;

  padding: 41px 0 13px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 10;
`;

const SelectItem = styled.div`
  padding: 0 17px;
  cursor: pointer;
  white-space: nowrap;
  text-align: start;
`;
